import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Leaf, ArrowLeft, Download, MapPin, Ruler, Sun, 
  TreePine, Calendar, Bell
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanDetail = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/garden-plans/${planId}`);
      setPlan(response.data);
    } catch (error) {
      console.error("Failed to fetch plan:", error);
      toast.error("Failed to load garden plan");
      navigate('/saved');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!plan) return;
    
    const exportData = {
      ...plan,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${plan.name.replace(/\s+/g, '_')}_garden_plan.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Garden plan exported!");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Plan not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/saved"
              className="text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground">
                {plan.name}
              </h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {plan.location}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/dashboard/${planId}`)}
              data-testid="dashboard-btn"
            >
              Full Dashboard
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-organic">
            <CardContent className="p-4 text-center">
              <Ruler className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Dimensions</p>
              <p className="font-mono font-semibold">
                {plan.width}×{plan.length}×{plan.height}m
              </p>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4 text-center">
              <TreePine className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Plants</p>
              <p className="font-mono font-semibold">
                {plan.plant_recommendations?.length || 0}
              </p>
            </CardContent>
          </Card>
          {plan.solar_data && (
            <>
              <Card className="card-organic">
                <CardContent className="p-4 text-center">
                  <Sun className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Summer Sun</p>
                  <p className="font-mono font-semibold">
                    {plan.solar_data.sun_hours_summer}h
                  </p>
                </CardContent>
              </Card>
              <Card className="card-organic">
                <CardContent className="p-4 text-center">
                  <Sun className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Winter Sun</p>
                  <p className="font-mono font-semibold">
                    {plan.solar_data.sun_hours_winter}h
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Description */}
        <Card className="card-organic">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Design Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="leading-relaxed">
              {plan.ai_description || "A sustainable permaculture garden design optimized for your location and preferences."}
            </p>
          </CardContent>
        </Card>

        {/* Plant List */}
        <Card className="card-organic">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Plant Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {(plan.plant_recommendations || []).map((plant, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{plant.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {plant.layer?.replace('_', ' ')} • {plant.sun_requirement?.replace('_', ' ')}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {plant.category}
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate(`/calendar/${planId}`)}
            data-testid="view-calendar-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            View Calendar
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate(`/reminders/${planId}`)}
            data-testid="view-reminders-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            Care Reminders
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PlanDetail;
